import { useCallback, useMemo, useState } from "react";
import { computeScore, resolveResultTier, progressLabel, progressPct, tallyPersonality } from "./quizScoring";
import type { QuizQuestionData, QuizResultLevel, QuizResult } from "./types";

type Phase = "quiz" | "results";

export function useQuizMachine(
  questions: QuizQuestionData[],
  resultLevels: QuizResultLevel[],
  quizType?: string | null,
  results: QuizResult[] = [],
) {
  const isPersonality = quizType === "personality";
  const total = questions.length;
  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>("quiz");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() => Array(total).fill(null));

  const select = useCallback((optionIndex: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[current] = optionIndex;
      return next;
    });
  }, [current]);

  const canAdvance = answers[current] != null;

  const goNext = useCallback(() => {
    if (answers[current] == null) return;
    if (current < total - 1) setCurrent((c) => c + 1);
    else setPhase("results");
  }, [answers, current, total]);

  const begin = useCallback(() => setStarted(true), []);

  const retake = useCallback(() => {
    setAnswers(Array(total).fill(null));
    setCurrent(0);
    setPhase("quiz");
    setStarted(false);
  }, [total]);

  // goBack: results -> last question; Q1 -> landing; else decrement
  const goBack = useCallback(() => {
    if (phase === "results") {
      setPhase("quiz");
      setCurrent(total - 1);
      return;
    }
    if (current === 0) {
      setStarted(false);
      return;
    }
    setCurrent((c) => c - 1);
  }, [phase, current, total]);

  const score = useMemo(() => computeScore(questions, answers), [questions, answers]);
  const resultTier = useMemo(() => resolveResultTier(resultLevels, score), [resultLevels, score]);
  // Falls back to the first-defined result: unmapped answers otherwise yield
  // null and render a blank results screen (no eyebrow/headline/message/score).
  const personalityResult = useMemo(
    () => (isPersonality ? tallyPersonality(questions, answers, results) ?? results[0] ?? null : null),
    [isPersonality, questions, answers, results],
  );

  const progressIndex = phase === "results" ? total - 1 : current;
  return {
    started, phase, current, answers, canAdvance,
    select, goNext, goBack, begin, retake,
    score, resultTier,
    isPersonality, personalityResult,
    progressLabel: progressLabel(progressIndex, total),
    progressPct: phase === "results" ? 100 : progressPct(current, total),
  };
}

// Note: begin/retake/goBack only mutate state. The GSAP "square-up" scroll and
// reverse-then-reset sequencing wrap these separately, so state transitions
// and animation orchestration stay decoupled.
